import { useState, useEffect } from "react";
import "@/App.css";
import { 
  Phone, 
  MapPin, 
  Clock, 
  Star, 
  Menu, 
  X, 
  ChevronRight, 
  Leaf, 
  Heart,
  Sparkles,
  ArrowRight,
  Mail,
  Calendar,
  Gift
} from "lucide-react";

// Business Data
const BUSINESS_INFO = {
  name: "NARA THAI MASSAGE",
  tagline: "AND WELLNESS CENTER",
  address: "12657 N Tatum Blvd Suite #133",
  city: "Phoenix, AZ 85032",
  locationNote: "Inside Trucco & Capelli Salon Suites (Left Hallway)",
  phone: "(480) 993-4214",
  hours: "Mon-Sat: 9AM - 8PM | Sun: 10AM - 6PM",
  rating: 5.0,
  reviewCount: 10,
  bookingUrl: "https://booksy.com/en-us/1721140_nara-thai-massage-and-wellness-center_massage_14717_phoenix#ba_s=seo",
  giftCardUrl: "https://booksy.com/en-us/1721140_nara-thai-massage-and-wellness-center_massage_14717_phoenix#ba_s=seo"
};

// Services Data
const SERVICES = [
  {
    id: 1,
    name: "Thai Renewal Massage",
    duration: "60 / 90 / 120 min",
    price: "$90 / $130 / $170",
    description: "Ancient healing technique combining stretching, pressure points, and rhythmic movements to restore energy flow and renew your body.",
    image: "https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?auto=format&fit=crop&q=80&w=600"
  },
  {
    id: 2,
    name: "Swedish Serenity Session",
    duration: "60 / 90 / 120 min",
    price: "$90 / $130 / $170",
    description: "Gentle, soothing strokes to melt away stress and promote complete mental and physical relaxation.",
    image: "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&q=80&w=600"
  },
  {
    id: 3,
    name: "Combination Massage",
    duration: "60 / 90 / 120 min",
    price: "$90 / $130 / $170",
    description: "A customized blend of Thai and Swedish techniques tailored to address your specific needs and preferences.",
    image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&q=80&w=600"
  },
  {
    id: 4,
    name: "Couples Massage",
    duration: "60 / 90 / 120 min",
    price: "$170 / $250 / $350",
    description: "Share a relaxing experience with your partner in our serene dual treatment space for ultimate connection.",
    image: "https://images.unsplash.com/photo-1600334089648-b0d9d3028eb2?auto=format&fit=crop&q=80&w=600"
  },
  {
    id: 5,
    name: "Prenatal Massage",
    duration: "60 / 90 / 120 min",
    price: "$90 / $130 / $170",
    description: "Gentle, nurturing massage designed specifically for expectant mothers to ease discomfort and promote relaxation.",
    image: "https://images.unsplash.com/photo-1610402601271-5b4bd5b3eba4?auto=format&fit=crop&q=80&w=600"
  }
];

// Add-Ons Data
const ADDONS = [
  {
    id: 1,
    name: "ZenStone Rebalance Add-On",
    price: "$20",
    description: "Warm stones placed on key energy points to enhance relaxation and restore balance.",
    icon: "stone"
  },
  {
    id: 2,
    name: "Lotus Purification Cupping Add-On",
    price: "$30",
    description: "Traditional cupping therapy to improve circulation and release deep muscle tension.",
    icon: "cup"
  },
  {
    id: 3,
    name: "Cannabliss Body Therapy Add-On",
    price: "$25",
    description: "CBD-infused treatment to reduce inflammation and promote deep muscle relief.",
    icon: "leaf"
  }
];

// Testimonials Data
const TESTIMONIALS = [
  {
    id: 1,
    name: "Sarah M.",
    text: "Ae is absolutely incredible! I've been following her for years. The attention to detail and focus on specific tension areas is unmatched. Best massage I've ever had.",
    date: "6 days ago"
  },
  {
    id: 2,
    name: "Michael R.",
    text: "The environment is so clean and relaxing. Ae really knows how to find and work on problem areas. I leave feeling like a new person every time.",
    date: "2 weeks ago"
  },
  {
    id: 3,
    name: "Jennifer L.",
    text: "Professional, friendly, and incredibly skilled. The traditional Thai massage here is authentic and therapeutic. Highly recommend!",
    date: "3 weeks ago"
  }
];

// Navigation Component
const Navigation = ({ isScrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "About", href: "#about" },
    { name: "Reviews", href: "#reviews" },
    { name: "Gift Cards", href: "#giftcards" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass border-b border-[#E2E8E2]/40 py-4' 
          : 'bg-transparent py-6'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a 
            href="#hero" 
            className="flex flex-col"
            data-testid="nav-logo"
          >
            <span className="text-xl font-semibold text-[#2C3E2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              NARA
            </span>
            <span className="text-[10px] tracking-[0.2em] text-[#787878] uppercase">
              Thai Massage
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              data-testid="nav-book-button"
            >
              <Calendar className="w-4 h-4" />
              Book Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#2C3E2D]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay open"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
        <button
          className="absolute top-6 right-6 p-2 text-[#2C3E2D]"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="flex flex-col gap-6 pt-12">
          {navLinks.map((link, index) => (
            <a
              key={link.name}
              href={link.href}
              className="text-lg font-medium text-[#2C3E2D] hover:text-[#4A6741]"
              onClick={() => setMobileMenuOpen(false)}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
            >
              {link.name}
            </a>
          ))}
          <a
            href={BUSINESS_INFO.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary mt-4"
            data-testid="mobile-book-button"
          >
            <Calendar className="w-4 h-4" />
            Book Appointment
          </a>
        </div>
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[#F5F9F5]">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#E8F3E8]/50 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Content */}
          <div className="lg:col-span-6 space-y-8">
            {/* Badge */}
            <div 
              className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-soft animate-fade-in-up"
              data-testid="hero-badge"
            >
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                ))}
              </div>
              <span className="text-sm font-medium text-[#2C3E2D]">
                {BUSINESS_INFO.rating} Rating • {BUSINESS_INFO.reviewCount} Reviews
              </span>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <p 
                className="text-sm tracking-[0.2em] uppercase text-[#4A6741] font-semibold animate-fade-in-up delay-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Authentic Thai Healing in Phoenix
              </p>
              <h1 
                className="text-5xl md:text-6xl lg:text-7xl font-medium text-[#2C3E2D] tracking-tight animate-fade-in-up delay-200"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="hero-headline"
              >
                Restore Your<br />
                <span className="text-[#4A6741]">Natural Balance</span>
              </h1>
            </div>

            {/* Description */}
            <p 
              className="text-lg text-[#787878] max-w-lg leading-relaxed animate-fade-in-up delay-300"
              data-testid="hero-description"
            >
              Experience the healing art of traditional Thai massage. Our expert therapist, Ae, 
              combines ancient techniques with modern wellness to help you achieve complete 
              relaxation and renewed energy.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 animate-fade-in-up delay-400">
              <a
                href={BUSINESS_INFO.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                data-testid="hero-book-button"
              >
                Book Your Session
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#services"
                className="btn-secondary"
                data-testid="hero-services-button"
              >
                View Services
              </a>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-6 pt-4 animate-fade-in-up delay-500">
              <div className="flex items-center gap-2 text-sm text-[#787878]">
                <MapPin className="w-4 h-4 text-[#4A6741]" />
                <span>Phoenix, AZ</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#787878]">
                <Clock className="w-4 h-4 text-[#4A6741]" />
                <span>Open until 8 PM</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-[#787878]">
                <Phone className="w-4 h-4 text-[#4A6741]" />
                <span>{BUSINESS_INFO.phone}</span>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="lg:col-span-6 relative animate-fade-in delay-300">
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-soft-lg hero-image-overlay">
                <img
                  src="https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&q=80&w=800&h=900"
                  alt="Relaxing Thai massage treatment"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                  data-testid="hero-image"
                />
              </div>

              {/* Floating Card */}
              <div 
                className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-soft-lg animate-float hidden lg:block"
                data-testid="hero-floating-card"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#E8F3E8] rounded-full flex items-center justify-center">
                    <Leaf className="w-6 h-6 text-[#4A6741]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C3E2D]">Years of Experience</p>
                    <p className="text-sm text-[#787878]">Traditional Thai Techniques</p>
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#D4AF37]/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section 
      id="services" 
      className="py-20 md:py-32 bg-[#FAFAF9] relative"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <p className="text-sm tracking-[0.2em] uppercase text-[#4A6741] font-semibold">
            Our Services
          </p>
          <h2 
            className="text-4xl md:text-5xl font-medium text-[#2C3E2D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="services-headline"
          >
            Healing Treatments
          </h2>
          <p className="text-lg text-[#787878] max-w-2xl mx-auto">
            Choose from our range of therapeutic treatments, each designed to restore 
            balance and promote wellness.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((service, index) => (
            <div 
              key={service.id} 
              className="service-card"
              data-testid={`service-card-${service.id}`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <img
                src={service.image}
                alt={service.name}
                className="service-card-image"
                loading="lazy"
              />
              <div className="flex-1 flex flex-col">
                <h3 
                  className="text-xl font-medium text-[#2C3E2D] mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {service.name}
                </h3>
                <p className="text-[#787878] text-sm mb-4 flex-1">
                  {service.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-[#E2E8E2]">
                  <div className="flex items-center gap-2 text-sm text-[#787878]">
                    <Clock className="w-4 h-4" />
                    <span>{service.duration}</span>
                  </div>
                  <span className="text-lg font-semibold text-[#4A6741]">
                    {service.price}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add-Ons Section */}
        <div className="mt-20">
          <div className="text-center mb-12 space-y-4">
            <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">
              Enhance Your Experience
            </p>
            <h3 
              className="text-3xl md:text-4xl font-medium text-[#2C3E2D]"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="addons-headline"
            >
              Add-Ons
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {ADDONS.map((addon, index) => (
              <div 
                key={addon.id}
                className="bg-[#F5F9F5] p-6 rounded-2xl border border-[#E2E8E2] hover:border-[#D4AF37] transition-all duration-300 hover:shadow-lg text-center"
                data-testid={`addon-card-${addon.id}`}
              >
                <div className="w-14 h-14 bg-[#E8F3E8] rounded-full flex items-center justify-center mx-auto mb-4">
                  {addon.icon === 'stone' && <Sparkles className="w-7 h-7 text-[#4A6741]" />}
                  {addon.icon === 'cup' && <Heart className="w-7 h-7 text-[#4A6741]" />}
                  {addon.icon === 'leaf' && <Leaf className="w-7 h-7 text-[#4A6741]" />}
                </div>
                <h4 
                  className="text-lg font-medium text-[#2C3E2D] mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {addon.name}
                </h4>
                <p className="text-sm text-[#787878] mb-4">
                  {addon.description}
                </p>
                <span className="text-2xl font-bold text-[#D4AF37]">
                  {addon.price}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href={BUSINESS_INFO.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            data-testid="services-book-button"
          >
            Book Your Treatment
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section 
      id="about" 
      className="py-20 md:py-32 bg-[#F5F9F5] relative overflow-hidden"
      data-testid="about-section"
    >
      {/* Background Pattern */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4A6741]/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-[2rem] overflow-hidden shadow-soft-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_nara-massage-booking/artifacts/c594n46d_C861611E-B3C2-4D4A-B3B7-8B2458B8E75F.png"
                alt="Ae - Lead Therapist at Nara Thai Massage"
                className="w-full h-[500px] lg:h-[600px] object-cover"
                data-testid="about-image"
              />
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -bottom-4 -right-4 bg-white p-5 rounded-2xl shadow-soft-lg">
              <div className="flex items-center gap-3">
                <Heart className="w-8 h-8 text-[#D4AF37]" />
                <div>
                  <p className="text-2xl font-bold text-[#2C3E2D]">10+</p>
                  <p className="text-sm text-[#787878]">Years Experience</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-sm tracking-[0.2em] uppercase text-[#4A6741] font-semibold">
                About Your Therapist
              </p>
              <h2 
                className="text-4xl md:text-5xl font-medium text-[#2C3E2D]"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="about-headline"
              >
                Meet Ae
              </h2>
              <div className="decorative-line" />
            </div>

            <div className="space-y-6 text-[#787878]">
              <p className="text-lg leading-relaxed" data-testid="about-description">
                With years of experience in traditional Thai healing arts, Ae brings authentic 
                wellness techniques directly from Thailand to Phoenix. Her passion for massage 
                therapy began at a young age, learning from masters in her homeland.
              </p>
              <p className="text-lg leading-relaxed">
                At Nara Thai Massage, every session is personalized to address your specific 
                needs. Whether you're seeking relief from chronic tension, recovering from 
                an injury, or simply need a moment of peace, Ae's intuitive approach ensures 
                you leave feeling renewed.
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#E8F3E8] rounded-full flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-[#4A6741]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2C3E2D]">Certified Expert</h4>
                  <p className="text-sm text-[#787878]">Traditional Thai massage certification</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#E8F3E8] rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-5 h-5 text-[#4A6741]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#2C3E2D]">Personal Care</h4>
                  <p className="text-sm text-[#787878]">Customized treatment for each client</p>
                </div>
              </div>
            </div>

            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex"
              data-testid="about-book-button"
            >
              Book with Ae
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section 
      id="reviews" 
      className="py-20 md:py-32 bg-[#FAFAF9]"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <p className="text-sm tracking-[0.2em] uppercase text-[#4A6741] font-semibold">
            Testimonials
          </p>
          <h2 
            className="text-4xl md:text-5xl font-medium text-[#2C3E2D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="testimonials-headline"
          >
            What Our Clients Say
          </h2>
          <div className="flex items-center justify-center gap-3 pt-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <span className="text-[#787878]">
              {BUSINESS_INFO.rating} out of 5 based on {BUSINESS_INFO.reviewCount} reviews
            </span>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((testimonial, index) => (
            <div 
              key={testimonial.id} 
              className="testimonial-card"
              data-testid={`testimonial-card-${testimonial.id}`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Stars */}
              <div className="star-rating mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </div>

              {/* Quote */}
              <p 
                className="text-[#2C3E2D] leading-relaxed mb-6 italic"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.125rem' }}
              >
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between pt-4 border-t border-[#E2E8E2]/50">
                <span className="font-semibold text-[#2C3E2D]">{testimonial.name}</span>
                <span className="text-sm text-[#787878]">{testimonial.date}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Google Reviews CTA */}
        <div className="text-center mt-12">
          <a
            href="https://www.google.com/search?q=nara+thai+massage+phoenix+az+review"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            data-testid="leave-review-button"
          >
            Leave a Review on Google
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

// Gift Card Section
const GiftCardSection = () => {
  return (
    <section 
      id="giftcards" 
      className="py-20 md:py-32 bg-[#FAFAF9] relative overflow-hidden"
      data-testid="giftcard-section"
    >
      {/* Background Decorations */}
      <div className="absolute top-10 right-10 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-2xl" />
      <div className="absolute bottom-10 left-10 w-40 h-40 bg-[#4A6741]/10 rounded-full blur-2xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <p className="text-sm tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">
                The Perfect Present
              </p>
              <h2 
                className="text-4xl md:text-5xl font-medium text-[#2C3E2D]"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="giftcard-headline"
              >
                Give the Gift of<br />
                <span className="text-[#4A6741]">Relaxation</span>
              </h2>
              <div className="decorative-line" />
            </div>

            <p className="text-lg text-[#787878] leading-relaxed">
              Treat someone special to the ultimate wellness experience. Our gift cards 
              are perfect for birthdays, anniversaries, holidays, or just to show you care. 
              Available in any amount and redeemable for all our massage services.
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E8F3E8] rounded-full flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#4A6741]" />
                </div>
                <span className="text-[#2C3E2D] font-medium">Available in any amount</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E8F3E8] rounded-full flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#4A6741]" />
                </div>
                <span className="text-[#2C3E2D] font-medium">Redeemable for all services</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E8F3E8] rounded-full flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#4A6741]" />
                </div>
                <span className="text-[#2C3E2D] font-medium">Never expires</span>
              </div>
            </div>

            <a
              href={BUSINESS_INFO.giftCardUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex"
              data-testid="giftcard-buy-button"
            >
              <Gift className="w-5 h-5" />
              Purchase Gift Card
            </a>
          </div>

          {/* Gift Card Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-[#4A6741] to-[#2C3E2D] rounded-3xl p-8 shadow-soft-lg transform hover:scale-[1.02] transition-transform duration-300">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h3 
                      className="text-2xl font-medium text-white"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      NARA
                    </h3>
                    <p className="text-xs tracking-[0.2em] text-white/70 uppercase">
                      Thai Massage & Wellness
                    </p>
                  </div>
                  <Gift className="w-10 h-10 text-[#D4AF37]" />
                </div>
                
                <div className="space-y-2 mb-8">
                  <p className="text-white/60 text-sm">Gift Card Value</p>
                  <p className="text-4xl font-bold text-[#D4AF37]">$90 - $350</p>
                </div>
                
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-white/60 text-xs mb-1">Recipient</p>
                    <p className="text-white font-medium">Someone Special</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs mb-1">From</p>
                    <p className="text-white font-medium">With Love</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-[#D4AF37] text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
              Perfect Gift!
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section 
      id="contact" 
      className="py-20 md:py-32 bg-[#F5F9F5]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <p className="text-sm tracking-[0.2em] uppercase text-[#4A6741] font-semibold">
            Visit Us
          </p>
          <h2 
            className="text-4xl md:text-5xl font-medium text-[#2C3E2D]"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="contact-headline"
          >
            Find Your Sanctuary
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Map */}
          <div className="map-container h-[400px] lg:h-full" data-testid="contact-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3324.0744831991774!2d-111.97543068479396!3d33.61542998072566!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x872b6f7b1cdf8cd5%3A0x8c9c9c9c9c9c9c9c!2s12657%20N%20Tatum%20Blvd%2C%20Phoenix%2C%20AZ%2085032!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '400px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Nara Thai Massage Location"
            />
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Address Card */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E8F3E8] rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#4A6741]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C3E2D] mb-2">Location</h3>
                  <p className="text-[#787878]" data-testid="contact-address">
                    {BUSINESS_INFO.address}<br />
                    {BUSINESS_INFO.city}
                  </p>
                  <p className="text-sm text-[#D4AF37] mt-2 font-medium">
                    {BUSINESS_INFO.locationNote}
                  </p>
                </div>
              </div>
            </div>

            {/* Phone Card */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E8F3E8] rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#4A6741]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C3E2D] mb-2">Phone</h3>
                  <a 
                    href={`tel:${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="text-[#4A6741] hover:text-[#D4AF37] text-lg font-medium transition-colors"
                    data-testid="contact-phone"
                  >
                    {BUSINESS_INFO.phone}
                  </a>
                  <p className="text-sm text-[#787878] mt-1">
                    Call or text for appointments
                  </p>
                </div>
              </div>
            </div>

            {/* Hours Card */}
            <div className="contact-info-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#E8F3E8] rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#4A6741]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#2C3E2D] mb-2">Hours</h3>
                  <p className="text-[#787878]" data-testid="contact-hours">
                    {BUSINESS_INFO.hours}
                  </p>
                  <p className="text-sm text-[#4A6741] mt-2 font-medium">
                    Walk-ins welcome, appointments preferred
                  </p>
                </div>
              </div>
            </div>

            {/* Book Now CTA */}
            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full justify-center"
              data-testid="contact-book-button"
            >
              <Calendar className="w-5 h-5" />
              Book Your Appointment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="bg-[#2C3E2D] text-white py-16"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div className="space-y-4">
            <div>
              <h3 
                className="text-2xl font-medium"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                NARA
              </h3>
              <p className="text-xs tracking-[0.2em] text-white/60 uppercase">
                Thai Massage & Wellness
              </p>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Authentic Thai healing and wellness in the heart of Phoenix. 
              Restore your natural balance with our expert treatments.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-[#D4AF37]">Quick Links</h4>
            <div className="space-y-3">
              <a href="#services" className="footer-link block text-sm">Services</a>
              <a href="#about" className="footer-link block text-sm">About Ae</a>
              <a href="#reviews" className="footer-link block text-sm">Reviews</a>
              <a href="#contact" className="footer-link block text-sm">Contact</a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-[#D4AF37]">Contact</h4>
            <div className="space-y-3 text-sm text-white/70">
              <p>{BUSINESS_INFO.address}</p>
              <p>{BUSINESS_INFO.city}</p>
              <a 
                href={`tel:${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="footer-link block"
              >
                {BUSINESS_INFO.phone}
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-white/10 mb-8" />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/50">
          <p>© {currentYear} Nara Thai Massage and Wellness Center. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="footer-link">Privacy Policy</a>
            <a href="#" className="footer-link">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Floating CTA Button - Boosts conversions
const FloatingCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Phone Call Button */}
      <a
        href={`tel:${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`}
        className={`fixed bottom-6 left-6 z-40 bg-[#4A6741] text-white p-4 rounded-full shadow-lg hover:bg-[#2C3E2D] transition-all duration-300 hover:scale-110 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="floating-call-button"
        aria-label="Call NARA Thai Massage"
      >
        <Phone className="w-6 h-6" />
      </a>

      {/* Book Now Button */}
      <a
        href={BUSINESS_INFO.bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed bottom-6 right-6 z-40 bg-[#D4AF37] text-[#2C3E2D] px-6 py-4 rounded-full shadow-lg hover:bg-white transition-all duration-300 hover:scale-105 flex items-center gap-2 font-semibold ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="floating-book-button"
      >
        <Calendar className="w-5 h-5" />
        <span className="hidden sm:inline">Book Now</span>
      </a>
    </>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside or pressing escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="App">
      <Navigation 
        isScrolled={isScrolled} 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <main>
        <HeroSection />
        <ServicesSection />
        <AboutSection />
        <TestimonialsSection />
        <GiftCardSection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingCTA />
    </div>
  );
}

export default App;
